/** Roles & permissions editor. */
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  groupPermissions,
  type Permission,
  type Role,
  useCreateRole,
  useDeleteRole,
  usePermissions,
  useRoles,
  useUpdateRole,
} from "@/features/admin/identity/queries";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Textarea } from "@/shared/ui/form";
import { Badge } from "@/shared/ui/primitives";

/** Permission picker grouped by module with "select all" per module. */
export function PermissionMatrix({
  permissions,
  value,
  onChange,
  idPrefix = "perm",
}: {
  permissions: Permission[];
  value: string[];
  onChange: (codes: string[]) => void;
  idPrefix?: string;
}): ReactElement {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const groups = useMemo(() => groupPermissions(permissions), [permissions]);
  const selected = new Set(value);
  const toggle = (code: string, on: boolean): void => {
    const next = new Set(selected);
    if (on) {
      next.add(code);
    } else {
      next.delete(code);
    }
    onChange([...next]);
  };
  const toggleModule = (codes: string[], on: boolean): void => {
    const next = new Set(selected);
    for (const c of codes) {
      if (on) {
        next.add(c);
      } else {
        next.delete(c);
      }
    }
    onChange([...next]);
  };
  const needle = filter.trim().toLowerCase();
  return (
    <div className="space-y-3">
      <Input
        aria-label={t("roles.filterPermissions")}
        placeholder={t("roles.filterPermissions")}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <p className="text-xs text-muted-foreground">
        {t("roles.selectedCount", { count: value.length, total: permissions.length })}
      </p>
      <div className="max-h-[50vh] space-y-3 overflow-y-auto pe-1">
        {[...groups.entries()].map(([module, perms]) => {
          const visible = needle
            ? perms.filter((p) =>
                `${p.code} ${p.label} ${p.description}`.toLowerCase().includes(needle),
              )
            : perms;
          if (visible.length === 0) {
            return null;
          }
          const codes = perms.map((p) => p.code);
          const all = codes.every((c) => selected.has(c));
          const some = !all && codes.some((c) => selected.has(c));
          return (
            <fieldset key={module} className="rounded-md border p-3">
              <legend className="px-1 text-sm font-semibold capitalize">
                <Checkbox
                  id={`${idPrefix}-module-${module}`}
                  label={module}
                  checked={all}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = some;
                    }
                  }}
                  onChange={(e) => toggleModule(codes, e.target.checked)}
                />
              </legend>
              <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
                {visible.map((p) => (
                  <div key={p.code} className="flex items-start gap-2">
                    <Checkbox
                      id={`${idPrefix}-${p.code}`}
                      label={p.label}
                      checked={selected.has(p.code)}
                      onChange={(e) => toggle(p.code, e.target.checked)}
                    />
                    {p.description ? (
                      <span className="text-xs text-muted-foreground">{p.description}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}

interface RoleValues extends Record<string, unknown> {
  key: string;
  name: string;
  description: string;
}

/** Create / edit / clone a role. */
export function RoleDialog({
  role,
  cloneFrom,
  onClose,
}: {
  role?: Role;
  cloneFrom?: Role;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const create = useCreateRole();
  const update = useUpdateRole();
  const source = role ?? cloneFrom;
  const [codes, setCodes] = useState<string[]>(source?.permissions ?? []);
  const initial = useMemo<RoleValues>(
    () => ({
      key: role?.key ?? (cloneFrom ? `${cloneFrom.key}-copy` : ""),
      name: role?.name ?? (cloneFrom ? `${cloneFrom.name} (copy)` : ""),
      description: source?.description ?? "",
    }),
    [role, cloneFrom, source],
  );
  const form = useForm<RoleValues>({
    initial,
    schema: z.object({
      key: role ? z.string() : z.string().regex(/^[a-z0-9_-]{2,50}$/, t("roles.keyFormat")),
      name: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "role",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      if (role) {
        await update.mutateAsync({
          id: role.id,
          body: { name: v.name.trim(), description: v.description, permissions: codes },
        });
      } else {
        await create.mutateAsync({
          key: v.key,
          name: v.name.trim(),
          description: v.description,
          permissions: codes,
        });
      }
    });
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={role ? t("roles.edit") : t("roles.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      wide
    >
      {role?.is_system ? (
        <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
          {t("roles.systemWarning")}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="role-key" label={t("app.key")} error={form.errors.key} hint={t("roles.keyHint")}>
          <Input {...form.field("key")} disabled={!!role} />
        </Field>
        <Field id="role-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
      </div>
      <Field id="role-description" label={t("app.description")}>
        <Textarea {...form.field("description")} />
      </Field>
      {permissions.isPending ? (
        <LoadingState />
      ) : permissions.isError ? (
        <ErrorState error={permissions.error} />
      ) : (
        <PermissionMatrix permissions={permissions.data} value={codes} onChange={setCodes} />
      )}
    </FormDialog>
  );
}

/** Roles page. */
export function RolesPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const roles = useRoles();
  const remove = useDeleteRole();
  const [dialog, setDialog] = useState<{ role?: Role; cloneFrom?: Role } | null>(null);
  const manage = can("identity.role.manage");

  const columns: ColumnDef<Role>[] = [
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <div>
          <div className="font-medium">
            {c.row.original.name}{" "}
            {c.row.original.is_system ? <Badge tone="outline">{t("roles.system")}</Badge> : null}
          </div>
          <div className="font-mono text-xs text-muted-foreground">{c.row.original.key}</div>
        </div>
      ),
    },
    {
      id: "description",
      header: t("app.description"),
      cell: (c) => c.row.original.description || "—",
    },
    { id: "count", header: t("roles.permissions"), cell: (c) => c.row.original.permissions.length },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ role: c.row.original })}
                >
                  {t("app.edit")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ cloneFrom: c.row.original })}
                >
                  {t("roles.clone")}
                </Button>
                {!c.row.original.is_system ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (globalThis.confirm(t("app.confirmDelete"))) {
                        remove.mutate(c.row.original.id);
                      }
                    }}
                  >
                    {t("app.delete")}
                  </Button>
                ) : null}
              </div>
            ),
          } satisfies ColumnDef<Role>,
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title={t("roles.title")}
        subtitle={t("roles.subtitle")}
        actions={manage ? <Button onClick={() => setDialog({})}>{t("roles.new")}</Button> : null}
      />
      {dialog ? (
        <RoleDialog
          role={dialog.role}
          cloneFrom={dialog.cloneFrom}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {roles.isPending ? (
        <LoadingState />
      ) : roles.isError ? (
        <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />
      ) : (
        <CompleteDataTable
          columns={columns}
          data={roles.data}
          getSearchText={(role) => `${role.key} ${role.name}`}
          searchDescription={t("tablePilot.searchFields", {
            fields: `${t("app.key")}, ${t("app.name")}`,
          })}
          sortFields={[{ value: "name", label: t("app.name"), valueOf: (role) => role.name }]}
          minWidth={700}
          getRowId={(r) => r.id}
          total={roles.data.length}
        />
      )}
    </div>
  );
}
